import { FullStory, init, isInitialized } from '@fullstory/browser';
import {
  AnalyticsAdapter,
  EventBase,
  mapAnalyticsDomainToInteractionName,
  UserVars,
} from '@lifeway/frontend-analytics';

function getFullStoryOrgId(): string {
  return (window.config?.FULLSTORY_ORG_ID as string) || '';
}

function initFullStory(): void {
  const orgId = getFullStoryOrgId();
  if (!orgId || isInitialized()) {
    return;
  }

  init({
    orgId,
    host: 'fullstory.com',
    script: 'edge.fullstory.com/s/fs.js',
    namespace: 'FS',
    devMode: false,
  });
}

export class FullStoryAdapter implements AnalyticsAdapter {
  private context: Record<string, unknown> = {};
  private identified = false;

  constructor() {
    initFullStory();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(...events: EventBase<any, any, any>[]): void {
    if (!isInitialized()) return;

    for (const event of events) {
      FullStory('trackEvent', {
        name: mapAnalyticsDomainToInteractionName(event.domain),
        properties: {
          domain: event.domain,
          ...event.context,
          eventSubType: event.eventName,
        },
      });
    }
  }

  viewPage(pageName: string, properties?: Record<string, unknown>): void {
    if (!isInitialized()) return;
    FullStory('setProperties', { type: 'page', properties: { pageName, ...properties } });
  }

  updatePage(properties?: Record<string, unknown>): void {
    if (!isInitialized()) return;
    FullStory('setProperties', { type: 'page', properties: properties ?? {} });
  }

  anonymize(): Promise<void> {
    // A session that was never identified is already anonymous, and anonymize runs on
    // every render with no user, so dropping the identity is worth doing only once.
    if (isInitialized() && this.identified) {
      FullStory('setIdentity', { anonymous: true });
      this.identified = false;
    }
    return Promise.resolve();
  }

  identify(userVars: UserVars): Promise<void> {
    if (!isInitialized()) return Promise.resolve();

    FullStory('setIdentity', {
      uid: userVars.id,
      properties: { displayName: userVars.fullName, email: userVars.email, ...this.context },
    });
    this.identified = true;
    return Promise.resolve();
  }

  log(_logLevel: number, _message: string): void {}
  unloadPage(): void {}
  addSessionContext(_newContext: Record<string, unknown>): void {
    this.context = { ..._newContext };
  }
}
